import './styles.scss'

import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { Modal } from 'antd'

import B2becTranslation from '@/components/B2becTranslation'
import B2BecLink from '@/components/B2BLink'
import { useAppDispatch, useAppSelector } from '@/store/hook'
import { authenticationActions, selectUserId } from '@/store/slices/authenticationSlice'
import { setupActions } from '@/store/slices/setupSlice'

type ConfigurationModalProps = {
  open?: boolean
  title?: string
  content?: React.ReactNode
  onConfirmHandler: () => void
} & Omit<React.ComponentProps<typeof Modal>, 'open' | 'title' | 'onOk'>

const ConfigurationModal = ({
  open = false,
  onConfirmHandler,
  title = '',
  content,
}: ConfigurationModalProps) => {
  return (
    <Modal
      cancelButtonProps={{ style: { display: 'none' } }}
      className="configuration-popup__wrapper"
      open={open}
      title={title}
      okButtonProps={{
        className: 'kaercher-button configuration-popup__confirm-button',
      }}
      onOk={onConfirmHandler}
    >
      <div className="configuration-popup__maincontent">{content}</div>
    </Modal>
  )
}

const SAPDowntimeModal = ({ open = false, onClose }) => {
  const { t } = useTranslation()

  const onConfirmHandler = () => {
    onClose()
  }

  const modalContent = (
    <>
      <div className="mb-3">
        <B2becTranslation
          value="sapDownTimePopup.mainContent"
          isComponent
          htmlTags={[
            <a
              key="0"
              href="https://infonet.karcher.com/Portals/64/Update%20zum%20neuen%20Distributionszentrum%20in%20Marl.pdf"
              target="_blank"
              rel="noopener noreferrer"
              download
            >
              placeholder
            </a>,
          ]}
        />
      </div>
      <B2BecLink rel="stylesheet" to="/contact-form" onClick={onConfirmHandler}>
        {t('sapDownTimePopup.contactMyKarcherBusinessSupport')}
      </B2BecLink>
    </>
  )

  return (
    <ConfigurationModal
      content={modalContent}
      okText={t('sapDownTimePopup.ok')}
      open={open}
      title={t('sapDownTimePopup.title')}
      width={570}
      onConfirmHandler={onConfirmHandler}
    />
  )
}

const UnauthorizedUserModal = ({ open = false }) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const userId = useAppSelector(selectUserId)

  const handleLogout = () => {
    dispatch(authenticationActions.signOut({ navigate }))
  }

  const modalContent = (
    <>
      <p className="mb-3">{t('invalidUserPopup.unauthorized.mainContent')}</p>
      <B2BecLink to={`/self-registration/${userId}`}>
        {t('invalidUserPopup.unauthorized.supportLink')}
      </B2BecLink>
    </>
  )

  return (
    <ConfigurationModal
      closable={false}
      content={modalContent}
      okText={t('logout')}
      open={open}
      title={t('invalidUserPopup.unauthorized.title')}
      width={570}
      onConfirmHandler={handleLogout}
    />
  )
}

const MissingDataUserModal = ({ open }) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const handleLogout = () => {
    dispatch(authenticationActions.signOut({ navigate }))
    dispatch(setupActions.hideMissingDataUserModal())
  }

  const modalContent = <p className="mb-3">{t('invalidUserPopup.missingData.mainContent')}</p>

  return (
    <ConfigurationModal
      closable={false}
      content={modalContent}
      okText={t('logout')}
      open={open}
      title={t('invalidUserPopup.missingData.title')}
      width={570}
      onConfirmHandler={handleLogout}
    />
  )
}

const InProgressingUserModal = ({ open = false }) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const handleLogout = () => {
    dispatch(authenticationActions.signOut({ navigate }))
  }

  const modalContent = <p className="mb-3">{t('invalidUserPopup.inProgress.mainContent')}</p>

  return (
    <ConfigurationModal
      closable={false}
      content={modalContent}
      okText={t('logout')}
      open={open}
      title={t('invalidUserPopup.inProgress.title')}
      width={570}
      onConfirmHandler={handleLogout}
    />
  )
}

export default ConfigurationModal

export { InProgressingUserModal, MissingDataUserModal, SAPDowntimeModal, UnauthorizedUserModal }
